import { WeaponType } from '../models/enums';
import { Filter } from '../models/filter-models';

const SECONDARY_WEAPON_CLASSES = [WeaponType.Quivers.toString(), WeaponType.Foci.toString()];

// Union of shown favourite weapon filters and leveling weapon classes.
export function collectMyGearWeaponClasses(filter: Filter, allWeaponClasses: string[]): Set<string> | null {
  const classes = new Set<string>();

  for (const weaponFilter of filter.weaponFilters) {
    if (!weaponFilter.show) continue;
    for (const weaponType of weaponFilter.weaponType) {
      if (weaponType !== WeaponType.All) {
        classes.add(weaponType.toString());
      }
    }
  }

  if (filter.showLevelingGearWeapons && filter.showLevelingGearWeaponClasses?.length) {
    if (filter.showLevelingGearWeaponClasses.includes(WeaponType.All)) {
      allWeaponClasses.forEach(c => classes.add(c));
    } else {
      for (const weaponType of filter.showLevelingGearWeaponClasses) {
        classes.add(weaponType.toString());
      }
    }
  }

  return classes.size > 0 ? classes : null;
}

// Prefer real weapons over Quivers/Foci when both are in the selection.
export function pickPreferredWeaponClass(
  classes: string[],
  isAvailable: (c: string) => boolean,
  secondary = SECONDARY_WEAPON_CLASSES,
): string | undefined {
  const preferred = classes.find(c => !secondary.includes(c) && isAvailable(c));
  if (preferred) return preferred;
  return classes.find(isAvailable);
}

// Leveling classes first, then shown favourite weapons.
// When leveling is off and no favourites are enabled, fall back to disabled weapon rules.
export function resolvePreferredWeaponClass(filter: Filter, availableWeaponClasses: string[]): string | undefined {
  const isAvailable = (c: string) => availableWeaponClasses.includes(c);

  if (filter.showLevelingGearWeapons && filter.showLevelingGearWeaponClasses?.length) {
    const classes = filter.showLevelingGearWeaponClasses;
    if (!classes.includes(WeaponType.All)) {
      const fromLeveling = pickPreferredWeaponClass(classes.map(c => c.toString()), isAvailable);
      if (fromLeveling) return fromLeveling;
    }
  }

  const shownWeaponTypes: string[] = [];
  const disabledWeaponTypes: string[] = [];
  for (const weaponFilter of filter.weaponFilters) {
    for (const weaponType of weaponFilter.weaponType) {
      if (weaponType === WeaponType.All) continue;
      if (weaponFilter.show) {
        shownWeaponTypes.push(weaponType.toString());
      } else {
        disabledWeaponTypes.push(weaponType.toString());
      }
    }
  }

  const fromShown = pickPreferredWeaponClass(shownWeaponTypes, isAvailable);
  if (fromShown) return fromShown;

  if (!filter.showLevelingGearWeapons) {
    return pickPreferredWeaponClass(disabledWeaponTypes, isAvailable);
  }

  return undefined;
}
